import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { API_URL } from "../config";

const SystemStatusPage = ({ theme, lang }) => {
  const [statusData, setStatusData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  
  // Logs state
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logStats, setLogStats] = useState(null);
  const [categories, setCategories] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedLevels, setSelectedLevels] = useState([]);
  const [logSearch, setLogSearch] = useState("");
  const [logSource, setLogSource] = useState("all");
  const [timeRange, setTimeRange] = useState("24h");
  const [useDbLogs, setUseDbLogs] = useState(false);
  const [pagination, setPagination] = useState({ limit: 200, offset: 0, total: 0, hasMore: false });
  const [loadingMore, setLoadingMore] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const [activeTab, setActiveTab] = useState("overview"); // overview, logs, errors
  const [wsConnected, setWsConnected] = useState(false);
  
  const logsEndRef = useRef(null);
  const wsRef = useRef(null);

  const darkTheme = {
    bg: "#1a1a2e",
    fg: "#ffffff",
    card: "#16213e",
    border: "#667eea",
    success: "#10b981",
    warning: "#f59e0b",
    error: "#ef4444",
    info: "#3b82f6",
    debug: "#6366f1",
    critical: "#dc2626",
  };

  const lightTheme = {
    bg: "#ffffff",
    fg: "#333333",
    card: "#f8f9fa",
    border: "#667eea",
    success: "#10b981",
    warning: "#f59e0b",
    error: "#ef4444",
    info: "#3b82f6",
    debug: "#6366f1",
    critical: "#dc2626",
  };

  const themeVars = theme === "dark" ? darkTheme : lightTheme;

  const logKey = (log) => log.id || `${log.created_at}-${log.level}-${log.category}-${log.message}`;

  const mergeLogs = (existing, incoming, { prepend = false, append = false } = {}) => {
    const seen = new Set(existing.map(logKey));
    const uniqueIncoming = incoming.filter(log => !seen.has(logKey(log)));
    if (prepend) return [...uniqueIncoming, ...existing];
    if (append) return [...existing, ...uniqueIncoming];
    return [...existing, ...uniqueIncoming];
  };

  const normalizeLogs = (logList) => {
    const safe = (logList || []).filter(Boolean);
    return [...safe].sort((a, b) => {
      const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
      const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;
      return aTime - bTime;
    });
  };

  const getStartTimeISO = (range) => {
    const hoursMap = { "1h": 1, "6h": 6, "24h": 24, "7d": 168, "30d": 720 };
    if (!range || range === "all") return null;
    const hours = hoursMap[range];
    if (!hours) return null;
    return new Date(Date.now() - hours * 60 * 60 * 1000).toISOString();
  };

  const formatTime = (value) => {
    if (!value) return "--:--:--";
    const date = new Date(value);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  };

  const formatDateTime = (value) => {
    if (!value) return "";
    const date = new Date(value);
    return date.toLocaleString();
  };

  // Scroll to bottom when new logs arrive
  useEffect(() => {
    if (autoScroll && logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [logs, autoScroll]);

  useEffect(() => {
    fetchStatus();
    fetchCategories();
    fetchLogStats();
    fetchRecentLogs();
    
    // Обновляем каждые 30 секунд
    const interval = setInterval(() => {
      fetchStatus();
      fetchLogStats();
    }, 30000);
    
    return () => {
      clearInterval(interval);
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  // WebSocket connection for real-time logs
  const connectWebSocket = useCallback(() => {
    const token = localStorage.getItem("token");
    if (!token) return;

    const wsUrl = API_URL.replace("http", "ws").replace("https", "wss");
    const ws = new WebSocket(`${wsUrl}/ws/logs/${token}`);

    ws.onopen = () => {
      console.log("Logs WebSocket connected");
      setWsConnected(true);
      
      // Request recent logs
      ws.send(JSON.stringify({ type: "get_recent", limit: 50 }));
    };

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        
        if (message.type === "log") {
          setLogs(prev => {
            const merged = mergeLogs(prev, [message.data], { append: true });
            return normalizeLogs(merged).slice(-500);
          });
        } else if (message.type === "recent_logs") {
          setLogs(normalizeLogs(message.data));
        }
      } catch (e) {
        console.error("Error parsing WS message:", e);
      }
    };

    ws.onclose = () => {
      console.log("Logs WebSocket disconnected");
      setWsConnected(false);
      // Reconnect after 5 seconds
      setTimeout(connectWebSocket, 5000);
    };

    ws.onerror = (error) => {
      console.error("WebSocket error:", error);
      setWsConnected(false);
    };

    wsRef.current = ws;
  }, []);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/api/system_status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStatusData(response.data);
      setLastUpdate(new Date());
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Ошибка загрузки статуса");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/api/logs/categories`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCategories(response.data);
    } catch (err) {
      console.error("Error fetching categories:", err);
    }
  };

  const fetchLogStats = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/api/logs/statistics?hours=24`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLogStats(response.data);
    } catch (err) {
      console.error("Error fetching log stats:", err);
    }
  };

  const fetchRecentLogs = async () => {
    try {
      setLogsLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/api/logs/recent?limit=200`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLogs(normalizeLogs(response.data.logs || []));
      setPagination({ limit: 200, offset: 0, total: response.data.count || 0, hasMore: false });
      setUseDbLogs(false);
    } catch (err) {
      console.error("Error fetching recent logs:", err);
    } finally {
      setLogsLoading(false);
    }
  };

  const fetchLogsFromDb = async ({ reset = false } = {}) => {
    try {
      setLoadingMore(true);
      const token = localStorage.getItem("token");
      const nextOffset = reset ? 0 : pagination.offset + pagination.limit;
      const startTime = getStartTimeISO(timeRange);
      const response = await axios.get(`${API_URL}/api/logs`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          limit: pagination.limit,
          offset: nextOffset,
          categories: selectedCategories.length > 0 ? selectedCategories.join(",") : undefined,
          levels: selectedLevels.length > 0 ? selectedLevels.join(",") : undefined,
          source: logSource !== "all" ? logSource : undefined,
          search: logSearch.trim() || undefined,
          start_time: startTime || undefined,
        },
      });
      const incoming = normalizeLogs(response.data.logs || []);
      setLogs(prev => {
        if (reset) return incoming;
        return mergeLogs(prev, incoming, { prepend: true });
      });
      setPagination({
        limit: response.data.pagination?.limit || pagination.limit,
        offset: response.data.pagination?.offset ?? nextOffset,
        total: response.data.pagination?.total || 0,
        hasMore: response.data.pagination?.has_more || false,
      });
      setUseDbLogs(true);
    } catch (err) {
      console.error("Error fetching logs from DB:", err);
    } finally {
      setLoadingMore(false);
    }
  };

  const getStatusColor = (status) => {
    if (["connected", "configured", "operational", "healthy"].includes(status)) {
      return themeVars.success;
    }
    if (["error", "failed", "unhealthy"].includes(status)) {
      return themeVars.error;
    }
    return themeVars.warning;
  };

  const getStatusIcon = (status) => {
    if (["connected", "configured", "operational", "healthy"].includes(status)) {
      return "✅";
    }
    if (["error", "failed", "unhealthy"].includes(status)) {
      return "❌";
    }
    return "⚠️";
  };

  const getLevelColor = (level) => {
    const colors = {
      debug: themeVars.debug,
      info: themeVars.success,
      warning: themeVars.warning,
      error: themeVars.error,
      critical: themeVars.critical,
    };
    return colors[level] || themeVars.fg;
  };

  const getLevelBackground = (level) => {
    const base = getLevelColor(level);
    return base ? `${base}14` : "transparent";
  };

  const getCategoryIcon = (category) => {
    const icons = {
      database: "🗄️",
      api: "🌐",
      websocket: "🔌",
      auth: "🔐",
      oauth: "🔑",
      matching: "💕",
      ml: "🤖",
      chat_ai: "💬",
      search: "🔍",
      invitations: "📧",
      events: "📅",
      profile: "👤",
      messages: "✉️",
      frontend_ui: "🖥️",
      frontend_api: "📡",
      frontend_error: "🚨",
      frontend_perf: "⚡",
      system: "⚙️",
      startup: "🚀",
      scheduler: "⏰",
      security: "🛡️",
    };
    return icons[category] || "📋";
  };

  const filteredLogs = logs.filter(log => {
    if (selectedCategories.length > 0 && !selectedCategories.includes(log.category)) {
      return false;
    }
    if (selectedLevels.length > 0 && !selectedLevels.includes(log.level)) {
      return false;
    }
    if (logSource !== "all" && log.source !== logSource) {
      return false;
    }
    if (logSearch.trim() && !(`${log.message} ${log.category} ${log.module} ${log.function}`.toLowerCase().includes(logSearch.trim().toLowerCase()))) {
      return false;
    }
    const startTime = getStartTimeISO(timeRange);
    if (startTime && log.created_at && new Date(log.created_at) < new Date(startTime)) {
      return false;
    }
    return true;
  });

  const StatCard = ({ title, value, subtitle, status, children }) => (
    <div
      style={{
        background: themeVars.card,
        border: `1px solid ${themeVars.border}`,
        borderRadius: "12px",
        padding: "20px",
        marginBottom: "20px",
        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
        <h3 style={{ margin: 0, color: themeVars.fg, fontSize: "18px", fontWeight: "600" }}>{title}</h3>
        {status && (
          <span style={{ color: getStatusColor(status), fontSize: "24px", fontWeight: "bold" }}>
            {getStatusIcon(status)}
          </span>
        )}
      </div>
      {value !== undefined && (
        <div style={{ fontSize: "32px", fontWeight: "bold", color: themeVars.fg, marginBottom: "5px" }}>
          {value}
        </div>
      )}
      {subtitle && (
        <div style={{ fontSize: "14px", color: themeVars.fg, opacity: 0.7 }}>{subtitle}</div>
      )}
      {children}
    </div>
  );

  const StatusBadge = ({ status, label }) => (
    <span
      style={{
        display: "inline-block",
        padding: "4px 12px",
        borderRadius: "12px",
        fontSize: "12px",
        fontWeight: "600",
        backgroundColor: getStatusColor(status) + "20",
        color: getStatusColor(status),
        marginRight: "8px",
        marginBottom: "8px",
      }}
    >
      {label}: {status}
    </span>
  );

  const TabButton = ({ id, label, icon }) => (
    <button
      onClick={() => setActiveTab(id)}
      style={{
        background: activeTab === id ? themeVars.border : "transparent",
        color: activeTab === id ? "#fff" : themeVars.fg,
        border: `1px solid ${themeVars.border}`,
        padding: "10px 20px",
        borderRadius: "8px",
        cursor: "pointer",
        fontSize: "14px",
        marginRight: "10px",
        transition: "all 0.2s",
      }}
    >
      {icon} {label}
    </button>
  );

  const LogEntry = ({ log }) => (
    <div
      style={{
        padding: "12px 14px",
        borderBottom: `1px solid ${themeVars.border}20`,
        background: getLevelBackground(log.level),
        fontSize: "13px",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
        <span style={{ color: themeVars.fg, opacity: 0.6, minWidth: "80px", fontFamily: "monospace" }}>
          {formatTime(log.created_at)}
        </span>
        <span
          style={{
            color: getLevelColor(log.level),
            fontWeight: "700",
            padding: "2px 8px",
            borderRadius: "999px",
            border: `1px solid ${getLevelColor(log.level)}`,
            textTransform: "uppercase",
            fontSize: "11px",
            fontFamily: "monospace",
          }}
        >
          {log.level}
        </span>
        <span style={{ minWidth: "20px" }}>{getCategoryIcon(log.category)}</span>
        <span style={{ color: themeVars.fg, opacity: 0.75, fontFamily: "monospace" }}>
          [{log.category}]
        </span>
        {log.source && (
          <span
            style={{
              color: themeVars.fg,
              opacity: 0.6,
              fontSize: "11px",
              padding: "2px 6px",
              borderRadius: "6px",
              border: `1px solid ${themeVars.border}`,
            }}
          >
            {log.source}
          </span>
        )}
        {log.user_id && (
          <span style={{ color: themeVars.fg, opacity: 0.6, fontSize: "11px" }}>user#{log.user_id}</span>
        )}
        {log.request_id && (
          <span style={{ color: themeVars.fg, opacity: 0.6, fontSize: "11px", fontFamily: "monospace" }}>
            req:{log.request_id}
          </span>
        )}
      </div>
      <div style={{ marginTop: "6px", color: themeVars.fg, fontFamily: "monospace", fontSize: "13px" }}>
        {log.message}
      </div>
      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginTop: "6px", fontSize: "11px", opacity: 0.8 }}>
        {log.http_method && log.http_path && (
          <span style={{ color: themeVars.info }}>
            {log.http_method} {log.http_path}
          </span>
        )}
        {log.http_status && (
          <span
            style={{
              color: log.http_status >= 500 ? themeVars.error : log.http_status >= 400 ? themeVars.warning : themeVars.success,
            }}
          >
            status {log.http_status}
          </span>
        )}
        {log.http_duration_ms !== null && log.http_duration_ms !== undefined && (
          <span style={{ color: themeVars.fg, opacity: 0.7 }}>{log.http_duration_ms.toFixed(1)}ms</span>
        )}
        {log.module && (
          <span style={{ color: themeVars.fg, opacity: 0.7 }}>
            {log.module}{log.function ? `.${log.function}` : ""}
          </span>
        )}
        {log.ip_address && <span style={{ color: themeVars.fg, opacity: 0.7 }}>{log.ip_address}</span>}
        {log.created_at && <span style={{ color: themeVars.fg, opacity: 0.6 }}>{formatDateTime(log.created_at)}</span>}
      </div>
      {log.details && (
        <details style={{ marginTop: "8px" }}>
          <summary style={{ cursor: "pointer", fontSize: "12px", color: themeVars.info }}>
            Подробности
          </summary>
          <pre
            style={{
              background: themeVars.bg,
              padding: "10px",
              borderRadius: "6px",
              overflow: "auto",
              fontSize: "11px",
              marginTop: "6px",
            }}
          >
            {JSON.stringify(log.details, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );

  if (loading && !statusData) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: themeVars.bg,
          color: themeVars.fg,
          padding: "40px 20px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: "48px", marginBottom: "20px" }}>⏳</div>
          <div>Загрузка статуса системы...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: "100vh", background: themeVars.bg, color: themeVars.fg, padding: "40px 20px" }}>
        <div
          style={{
            background: themeVars.card,
            border: `1px solid ${themeVars.error}`,
            borderRadius: "12px",
            padding: "30px",
            maxWidth: "800px",
            margin: "0 auto",
          }}
        >
          <h2 style={{ color: themeVars.error, marginTop: 0 }}>❌ Ошибка</h2>
          <p>{error}</p>
          <button
            onClick={fetchStatus}
            style={{
              background: themeVars.error,
              color: "#fff",
              border: "none",
              padding: "10px 20px",
              borderRadius: "8px",
              cursor: "pointer",
              fontSize: "16px",
              marginTop: "20px",
            }}
          >
            Повторить попытку
          </button>
        </div>
      </div>
    );
  }

  const { systems, overall_status, timestamp } = statusData || {};

  return (
    <div style={{ minHeight: "100vh", background: themeVars.bg, color: themeVars.fg, padding: "40px 20px" }}>
      <div style={{ maxWidth: "1600px", margin: "0 auto" }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "30px", flexWrap: "wrap" }}>
          <div>
            <h1 style={{ margin: 0, fontSize: "32px", fontWeight: "bold" }}>📊 Статус системы</h1>
            {lastUpdate && (
              <p style={{ margin: "5px 0 0 0", opacity: 0.7, fontSize: "14px" }}>
                Последнее обновление: {lastUpdate.toLocaleTimeString()}
              </p>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
            <StatusBadge status={overall_status} label="Общий статус" />
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                padding: "4px 12px",
                borderRadius: "12px",
                fontSize: "12px",
                fontWeight: "600",
                backgroundColor: wsConnected ? themeVars.success + "20" : themeVars.error + "20",
                color: wsConnected ? themeVars.success : themeVars.error,
              }}
            >
              {wsConnected ? "🟢 Live" : "🔴 Offline"}
            </span>
            <button
              onClick={fetchStatus}
              style={{
                background: themeVars.info,
                color: "#fff",
                border: "none",
                padding: "10px 20px",
                borderRadius: "8px",
                cursor: "pointer",
                fontSize: "14px",
              }}
            >
              🔄 Обновить
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ marginBottom: "30px" }}>
          <TabButton id="overview" label="Обзор" icon="📊" />
          <TabButton id="logs" label="Логи" icon="📋" />
          <TabButton id="errors" label="Ошибки" icon="🚨" />
          {!wsConnected && (
            <button
              onClick={connectWebSocket}
              style={{
                background: themeVars.success,
                color: "#fff",
                border: "none",
                padding: "10px 20px",
                borderRadius: "8px",
                cursor: "pointer",
                fontSize: "14px",
                marginLeft: "20px",
              }}
            >
              🔌 Подключить Real-time
            </button>
          )}
        </div>

        {/* Overview Tab */}
        {activeTab === "overview" && (
          <>
            {/* Log Statistics */}
            {logStats && (
              <div style={{ marginBottom: "40px" }}>
                <h2 style={{ fontSize: "24px", marginBottom: "20px" }}>📈 Статистика логов (24ч)</h2>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "20px" }}>
                  <StatCard title="Всего логов" value={logStats.total_logs} />
                  <StatCard 
                    title="Ошибок за час" 
                    value={logStats.errors_last_hour} 
                    status={logStats.errors_last_hour > 10 ? "error" : logStats.errors_last_hour > 0 ? "warning" : "healthy"}
                  />
                  <StatCard 
                    title="Среднее время API" 
                    value={`${logStats.avg_api_duration_ms}ms`}
                    status={logStats.avg_api_duration_ms > 500 ? "warning" : "healthy"}
                  />
                  <StatCard 
                    title="WS подключения" 
                    value={logStats.cache_stats?.ws_connections || 0}
                  />
                </div>

                {/* Logs by Level */}
                {logStats.by_level && Object.keys(logStats.by_level).length > 0 && (
                  <div style={{ marginTop: "20px" }}>
                    <h4 style={{ fontSize: "16px", marginBottom: "15px" }}>По уровням</h4>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                      {Object.entries(logStats.by_level).map(([level, count]) => (
                        <div
                          key={level}
                          style={{
                            background: getLevelColor(level) + "20",
                            border: `1px solid ${getLevelColor(level)}`,
                            borderRadius: "8px",
                            padding: "10px 15px",
                            fontSize: "14px",
                            color: getLevelColor(level),
                          }}
                        >
                          <strong style={{ textTransform: "uppercase" }}>{level}:</strong> {count}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Logs by Category */}
                {logStats.by_category && Object.keys(logStats.by_category).length > 0 && (
                  <div style={{ marginTop: "20px" }}>
                    <h4 style={{ fontSize: "16px", marginBottom: "15px" }}>По категориям</h4>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                      {Object.entries(logStats.by_category)
                        .sort((a, b) => b[1] - a[1])
                        .slice(0, 12)
                        .map(([category, count]) => (
                        <div
                          key={category}
                          style={{
                            background: themeVars.card,
                            border: `1px solid ${themeVars.border}`,
                            borderRadius: "8px",
                            padding: "10px 15px",
                            fontSize: "14px",
                          }}
                        >
                          {getCategoryIcon(category)} <strong>{category}:</strong> {count}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Database Status */}
            {systems?.database && (
              <div>
                <h2 style={{ fontSize: "24px", marginBottom: "20px", marginTop: "30px" }}>🗄️ База данных</h2>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "20px" }}>
                  <StatCard title="Подключение" status={systems.database.status} value={systems.database.connection === "ok" ? "Подключено" : "Ошибка"} />
                  <StatCard title="Пользователи" value={systems.database.statistics?.users} />
                  <StatCard title="Матчи" value={systems.database.statistics?.matches} />
                  <StatCard title="Сообщения" value={systems.database.statistics?.messages} />
                  <StatCard title="Активные профили" value={systems.database.statistics?.active_profiles} />
                  <StatCard title="Запросы на поиск" value={systems.database.statistics?.search_requests} />
                </div>
              </div>
            )}

            {/* External Services */}
            <div style={{ marginTop: "40px" }}>
              <h2 style={{ fontSize: "24px", marginBottom: "20px" }}>🔌 Внешние сервисы</h2>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "20px" }}>
                {systems?.openai && (
                  <StatCard title="OpenAI API" status={systems.openai.status} subtitle={systems.openai.api_key_set ? "API ключ настроен" : "API ключ не настроен"} />
                )}
                {systems?.ml_api && (
                  <StatCard title="ML API" status={systems.ml_api.status} subtitle={systems.ml_api.enabled ? "Включен" : "Отключен"}>
                    <div style={{ fontSize: "14px", marginTop: "10px" }}>
                      Доступен: {systems.ml_api.available ? "✅ Да" : "❌ Нет"}
                    </div>
                  </StatCard>
                )}
                {systems?.websocket && (
                  <StatCard title="WebSocket" status={systems.websocket.status} value={systems.websocket.active_connections} subtitle="активных соединений" />
                )}
              </div>
            </div>
          </>
        )}

        {/* Logs Tab */}
        {activeTab === "logs" && (
          <div>
            {/* Filters */}
            <div style={{ marginBottom: "20px", display: "flex", flexWrap: "wrap", gap: "15px", alignItems: "center" }}>
              {/* Level Filter */}
              <div>
                <span style={{ fontSize: "14px", marginRight: "10px" }}>Уровень:</span>
                {categories?.levels?.map(level => (
                  <button
                    key={level.id}
                    onClick={() => {
                      setSelectedLevels(prev => 
                        prev.includes(level.id) 
                          ? prev.filter(l => l !== level.id)
                          : [...prev, level.id]
                      );
                    }}
                    style={{
                      background: selectedLevels.includes(level.id) ? level.color : "transparent",
                      color: selectedLevels.includes(level.id) ? "#fff" : level.color,
                      border: `1px solid ${level.color}`,
                      padding: "4px 10px",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "12px",
                      marginRight: "5px",
                      textTransform: "uppercase",
                    }}
                  >
                    {level.name}
                  </button>
                ))}
              </div>

              {/* Category Filter */}
              <div>
                <span style={{ fontSize: "14px", marginRight: "10px" }}>Категория:</span>
                <select
                  multiple
                  value={selectedCategories}
                  onChange={(e) => {
                    const selected = Array.from(e.target.selectedOptions, opt => opt.value);
                    setSelectedCategories(selected);
                  }}
                  style={{
                    background: themeVars.card,
                    color: themeVars.fg,
                    border: `1px solid ${themeVars.border}`,
                    borderRadius: "4px",
                    padding: "5px",
                    fontSize: "12px",
                    minWidth: "150px",
                    maxHeight: "100px",
                  }}
                >
                  {categories?.categories?.map(cat => (
                    <option key={cat.id} value={cat.id}>
                      {getCategoryIcon(cat.id)} {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Search */}
              <div>
                <span style={{ fontSize: "14px", marginRight: "10px" }}>Поиск:</span>
                <input
                  type="text"
                  value={logSearch}
                  onChange={(e) => setLogSearch(e.target.value)}
                  placeholder="Текст, модуль, категория..."
                  style={{
                    background: themeVars.card,
                    color: themeVars.fg,
                    border: `1px solid ${themeVars.border}`,
                    borderRadius: "4px",
                    padding: "6px 8px",
                    fontSize: "12px",
                    minWidth: "220px",
                  }}
                />
              </div>

              {/* Source Filter */}
              <div>
                <span style={{ fontSize: "14px", marginRight: "10px" }}>Источник:</span>
                <select
                  value={logSource}
                  onChange={(e) => setLogSource(e.target.value)}
                  style={{
                    background: themeVars.card,
                    color: themeVars.fg,
                    border: `1px solid ${themeVars.border}`,
                    borderRadius: "4px",
                    padding: "6px",
                    fontSize: "12px",
                  }}
                >
                  <option value="all">Все</option>
                  <option value="backend">Backend</option>
                  <option value="frontend">Frontend</option>
                </select>
              </div>

              {/* Time Range */}
              <div>
                <span style={{ fontSize: "14px", marginRight: "10px" }}>Период:</span>
                <select
                  value={timeRange}
                  onChange={(e) => setTimeRange(e.target.value)}
                  style={{
                    background: themeVars.card,
                    color: themeVars.fg,
                    border: `1px solid ${themeVars.border}`,
                    borderRadius: "4px",
                    padding: "6px",
                    fontSize: "12px",
                  }}
                >
                  <option value="1h">1 час</option>
                  <option value="6h">6 часов</option>
                  <option value="24h">24 часа</option>
                  <option value="7d">7 дней</option>
                  <option value="30d">30 дней</option>
                  <option value="all">Все время</option>
                </select>
              </div>

              {/* Auto-scroll toggle */}
              <label style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "14px", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={autoScroll}
                  onChange={(e) => setAutoScroll(e.target.checked)}
                />
                Авто-прокрутка
              </label>

              {/* Clear filters */}
              {(selectedCategories.length > 0 || selectedLevels.length > 0) && (
                <button
                  onClick={() => {
                    setSelectedCategories([]);
                    setSelectedLevels([]);
                  }}
                  style={{
                    background: "transparent",
                    color: themeVars.error,
                    border: `1px solid ${themeVars.error}`,
                    padding: "4px 10px",
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontSize: "12px",
                  }}
                >
                  ✕ Сбросить фильтры
                </button>
              )}

              {/* Refresh button */}
              <button
                onClick={fetchRecentLogs}
                style={{
                  background: themeVars.info,
                  color: "#fff",
                  border: "none",
                  padding: "6px 12px",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "12px",
                }}
              >
                🔄 Обновить логи
              </button>

              {/* Search in DB */}
              <button
                onClick={() => fetchLogsFromDb({ reset: true })}
                style={{
                  background: themeVars.success,
                  color: "#fff",
                  border: "none",
                  padding: "6px 12px",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "12px",
                }}
              >
                📚 Поиск в БД
              </button>
            </div>

            {/* Logs Count */}
            <div style={{ marginBottom: "10px", fontSize: "14px", opacity: 0.7 }}>
              Показано: {filteredLogs.length} из {logs.length} логов
              {useDbLogs && pagination.total > 0 ? ` (в базе: ${pagination.total})` : ""}
            </div>

            {/* Logs List */}
            <div
              style={{
                background: themeVars.card,
                border: `1px solid ${themeVars.border}`,
                borderRadius: "12px",
                maxHeight: "600px",
                overflowY: "auto",
              }}
            >
              {logsLoading ? (
                <div style={{ padding: "40px", textAlign: "center" }}>Загрузка логов...</div>
              ) : filteredLogs.length === 0 ? (
                <div style={{ padding: "40px", textAlign: "center", opacity: 0.7 }}>
                  Логи не найдены
                </div>
              ) : (
                <>
                  {filteredLogs.map((log, index) => (
                    <LogEntry key={log.id || index} log={log} />
                  ))}
                  <div ref={logsEndRef} />
                </>
              )}
            </div>

            {/* Load more */}
            {useDbLogs && pagination.hasMore && (
              <div style={{ marginTop: "12px", display: "flex", justifyContent: "center" }}>
                <button
                  onClick={() => fetchLogsFromDb({ reset: false })}
                  disabled={loadingMore}
                  style={{
                    background: themeVars.border,
                    color: "#fff",
                    border: "none",
                    padding: "8px 16px",
                    borderRadius: "8px",
                    cursor: loadingMore ? "default" : "pointer",
                    fontSize: "13px",
                    opacity: loadingMore ? 0.6 : 1,
                  }}
                >
                  {loadingMore ? "Загрузка..." : "Загрузить еще"}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Errors Tab */}
        {activeTab === "errors" && (
          <div>
            <h2 style={{ fontSize: "24px", marginBottom: "20px" }}>🚨 Ошибки за последние 24 часа</h2>
            
            <div
              style={{
                background: themeVars.card,
                border: `1px solid ${themeVars.border}`,
                borderRadius: "12px",
                maxHeight: "600px",
                overflowY: "auto",
              }}
            >
              {logs.filter(log => log.level === "error" || log.level === "critical").length === 0 ? (
                <div style={{ padding: "40px", textAlign: "center" }}>
                  <div style={{ fontSize: "48px", marginBottom: "10px" }}>✅</div>
                  <div style={{ color: themeVars.success }}>Ошибок не обнаружено!</div>
                </div>
              ) : (
                logs
                  .filter(log => log.level === "error" || log.level === "critical")
                  .map((log, index) => (
                    <div
                      key={log.id || index}
                      style={{
                        padding: "15px",
                        borderBottom: `1px solid ${themeVars.border}20`,
                        background: log.level === "critical" ? themeVars.critical + "10" : themeVars.error + "10",
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                        <span style={{ color: getLevelColor(log.level), fontWeight: "600", textTransform: "uppercase" }}>
                          {log.level === "critical" ? "🔥" : "❌"} {log.level}
                        </span>
                        <span style={{ opacity: 0.7, fontSize: "12px" }}>{log.created_at}</span>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
                        <span>{getCategoryIcon(log.category)}</span>
                        <span style={{ opacity: 0.7 }}>[{log.category}]</span>
                        {log.http_path && <span style={{ color: themeVars.info }}>{log.http_method} {log.http_path}</span>}
                      </div>
                      <div style={{ fontFamily: "monospace", fontSize: "14px" }}>{log.message}</div>
                      {log.details && (
                        <details style={{ marginTop: "10px" }}>
                          <summary style={{ cursor: "pointer", fontSize: "12px", color: themeVars.info }}>
                            Подробности
                          </summary>
                          <pre
                            style={{
                              background: themeVars.bg,
                              padding: "10px",
                              borderRadius: "4px",
                              overflow: "auto",
                              fontSize: "11px",
                              marginTop: "5px",
                            }}
                          >
                            {JSON.stringify(log.details, null, 2)}
                          </pre>
                        </details>
                      )}
                    </div>
                  ))
              )}
            </div>
          </div>
        )}

        {/* Timestamp */}
        {timestamp && (
          <div
            style={{
              marginTop: "40px",
              padding: "15px",
              background: themeVars.card,
              borderRadius: "8px",
              textAlign: "center",
              fontSize: "12px",
              opacity: 0.7,
            }}
          >
            Данные получены: {new Date(timestamp).toLocaleString()}
          </div>
        )}
      </div>
    </div>
  );
};

export default SystemStatusPage;
